// owner/screens/unit/UnitCalendar.js — one month of a unit: booked / closed / open nights.
// The server answers by month (GET /api/v1/inventory/calendar/:id?month=) with the month's dates, their weekdays,
// Riyadh's today and the previous/next month keys, so this grid does no date arithmetic (the ops grid's rule).
// A night is booked when a guest holds it, closed when it cannot be sold, otherwise open.
// Arrows: «السابق» sits at the start of the reading direction and points that way (the kit flips both in English).

import React, { useState } from 'react';
import useLanguage from '../../../hooks/useLanguage';
import Icon from '../../ui/Icon';
import { InlineError } from '../../ui/States';
import useOwnerFormat from '../../ui/useOwnerFormat';
import { useUnitCalendar } from '../../api/ownerReads';

const nightState = (row) => {
  if (!row) return '';
  if ((row.booked || 0) > 0) return 'booked';
  if (!row.canBook) return 'closed';
  return 'open';
};

const UnitCalendar = ({ unitId }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const [month, setMonth] = useState('current');
  const cal = useUnitCalendar(unitId, month);
  const data = cal.data;

  if (cal.isLoading) return <div className="o-sk" style={{ height: 360, borderRadius: 20 }} aria-busy="true" />;
  if (cal.isError || !data?.dates) return <div className="o-sheet"><InlineError text={t('ownerApp.unit.calendar.error')} onRetry={cal.refetch} /></div>;

  const byDate = Object.fromEntries((data.calendar || []).map((row) => [row.date, row]));
  const weekdays = t('ownerApp.unit.calendar.weekdays');
  const leading = data.weekdays[0];  // blank cells before the 1st (0 = Sunday)

  return (
    <div className="o-sheet o-cal" aria-busy={cal.isFetching}>
      <div className="o-cal-head">
        <button type="button" onClick={() => setMonth(data.prevMonth)} aria-label={t('ownerApp.unit.calendar.prev')} disabled={cal.isFetching}>
          <Icon name="chevStart" size="sm" />
        </button>
        <h3>{f.month(data.month)}</h3>
        <button type="button" onClick={() => setMonth(data.nextMonth)} aria-label={t('ownerApp.unit.calendar.next')} disabled={cal.isFetching}>
          <Icon name="chevEnd" size="sm" />
        </button>
      </div>
      <div className="o-cal-grid" role="group" aria-label={f.month(data.month)}>
        {weekdays.map((name) => <span key={name} className="o-wd" aria-hidden="true">{name}</span>)}
        {Array.from({ length: leading }).map((_, i) => <span key={`pad-${i}`} aria-hidden="true" />)}
        {data.dates.map((date) => {
          const state = nightState(byDate[date]);
          const cls = ['o-day', state === 'open' ? '' : state, date < data.today ? 'past' : '', date === data.today ? 'today' : '']
            .filter(Boolean).join(' ');
          const label = `${f.date(date)}${state ? ` — ${t(`ownerApp.unit.calendar.${state}`)}` : ''}`;
          return <span key={date} className={cls} role="img" aria-label={label}>{f.dayOfMonth(date)}</span>;
        })}
      </div>
      <div className="o-cal-legend">
        <span><i style={{ background: 'var(--purple)' }} />{t('ownerApp.unit.calendar.booked')}</span>
        <span><i style={{ background: 'var(--g100)' }} />{t('ownerApp.unit.calendar.closed')}</span>
        <span><i style={{ background: 'var(--g50)', border: '1px solid var(--g200)' }} />{t('ownerApp.unit.calendar.open')}</span>
        <span><i style={{ boxShadow: 'inset 0 0 0 2px var(--orange)' }} />{t('ownerApp.unit.calendar.today')}</span>
      </div>
    </div>
  );
};

export default UnitCalendar;
